import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Marker, Polygon, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import { getIconMarkerCluster, getSVGIcon, getIconDeactivated, getIconVehicle, getOthersSVGIcon, getIconMarkerClusterOthers } from '../shared/helper';
import { useSelector, shallowEqual } from 'react-redux';
import "leaflet/dist/leaflet.css";
import 'react-leaflet-markercluster/styles'


function getTriggerMaxPriority(complex) {
    let priorities = (complex.triggers || []).map(trigger => parseInt(trigger.priority, 10));
    if (complex.child) {
        (complex.child || []).map(child => {
            priorities = [...priorities, ...(child.triggers || []).map(trigger => parseInt(trigger.priority, 10))]
        })
    }
    let maxPriority = Math.max(...priorities);
    if (!isFinite(maxPriority)) maxPriority = 0;
    // console.log(maxPriority)
    return maxPriority
}

const getPosition = (item, type) => {
    
    if (type === 'vehicle') {
        let lat = item.items.find(i => i.key_.includes('_lat'))?.lastvalue;
        let lon = item.items.find(i => i.key_.includes('_lon'))?.lastvalue;

        if (!lat && item.inventory.location_lat) {
            lat = item.inventory.location_lat;
        }
        if (!lon && item.inventory.location_lon) {
            lon = item.inventory.location_lon;
        }

        return lat && lon ? [lat, lon] : null;
    }
 
    // Для всех остальных типов
    return item.inventory.location_lat && item.inventory.location_lon
        ? [item.inventory.location_lat, item.inventory.location_lon]
        : null;
};

const getMarkerIcon = (item, triggerMaxPriority, type, pautinaStatus, speedLimit = null) => {
    switch (type) {
        case 'deactivated':
            return getIconDeactivated(item);
        case 'vehicle':
            return getIconVehicle(item);
        default:
            if (type.startsWith('@')) {
                return getOthersSVGIcon(item, pautinaStatus, speedLimit, speedLimit);
            }
            return getSVGIcon(item, triggerMaxPriority, pautinaStatus, speedLimit);
    }
};

const shouldShowTooltip = (complex, type) => {
    switch (type) {
        case 'crossroad':
            const info = complex.child?.filter(child => child.triggers.length !== 0);
            return info && info.length > 0;
        case 'speed':
        case 'vgk':
        case 'parking':
        case 'deactivated':
            return complex.macros?.find(item => item.macro === '{$POWER}')?.value === 'BAT';
        case 'vehicle':
            return false; // Vehicle маркеры обычно не показывают tooltip
        default:
            return false;
    }
};

const getZIndexOffset = (complex, triggerMaxPriority, type) => {
    let zIndexAdd = 0;

    if (type === 'crossroad') {
        complex.triggers?.forEach(trigger => {
            if (trigger.problem?.tags?.find(tag => tag.tag === 'many_violations')) {
                zIndexAdd = 300;
            }
        });
    }

    return triggerMaxPriority * 100 + zIndexAdd;
};

const checkSeverityFilter = (complex, type, filter) => {
    // Deactivated и vehicle маркеры не используют severity фильтр
    if (type === 'deactivated' || type === 'vehicle' || type.startsWith('@')) {
        return true;
    }

    if (!filter.severity || filter.severity <= 0) {
        return true;
    }

    const hasTrigger = complex.triggers?.find(t =>
        parseInt(t.priority, 10) >= filter.severity
    );

    if (hasTrigger) {
        return true;
    }

    // Для crossroad проверяем дочерние элементы
    if (type === 'crossroad' && complex.child) {
        return complex.child.some(c =>
            c.triggers?.find(t => parseInt(t.priority, 10) >= filter.severity)
        );
    }

    return false;
};

const getPautinaMaxStatus = (data) => {
    if (!data) return -1
    let maxStatus = 0
    if (data.poverka_expiring) maxStatus = 1
    if (!data.poverka || !data.status || !data.performance || !data.approved_vilations || data.schedule) maxStatus = 2

    return maxStatus
}

const Markers = ({ type = 'default', clustering = false, maxClusterRadius = 22, showCameraDirection = false, showSpeedLimit = false }) => {
    console.log('markers')
    const map = useMap();
    const clusterRef = useRef();
    const positionCacheRef = useRef(new Map());
    const polygonOptions = useMemo(() => ({ color: "#grey", weight: 1 }), []);
    const [mapState, setMapState] = useState({ zoom: map.getZoom(), bounds: map.getBounds() });

    useEffect(() => {
        const handleMapChange = () => {
            setMapState({ zoom: map.getZoom(), bounds: map.getBounds() });
        };

        map.on('zoomend', handleMapChange);
        map.on('moveend', handleMapChange);

        return () => {
            map.off('zoomend', handleMapChange);
            map.off('moveend', handleMapChange);
        };
    }, [map]);


    const handleMarkerClick = useCallback((item) => (e) => {
        map.fire('openPopup', { hostid: item.hostid, hostType: type })
    }, [map, type]);

    const objects = useSelector(state => state.store.objects, shallowEqual);
    const filter = useSelector(state => state.store.filter, shallowEqual);
    
    const dataSource = type === 'vehicle'
        ? (objects?.vehicles || [])
        : type.startsWith('@')
            ? (objects?.others?.[type] || [])
            : (objects?.[type] || []);

    const createClusterCustomIcon = useCallback((cluster) => {
        if (!cluster) return null
        if (!type.startsWith('@')) {
            const triggerPriorities = [];
            const statuses = []
            cluster.getAllChildMarkers().forEach(child => {
                triggerPriorities.push(child.options.trigger);
                statuses.push(child.options.pautinaStatus);
            });
            return getIconMarkerCluster(cluster.getAllChildMarkers().length, triggerPriorities, statuses);
        } else {
            const statuses = []
            cluster.getAllChildMarkers().forEach(child => {
                statuses.push(child.options.pautinaStatus)
            });
            return getIconMarkerClusterOthers(cluster.getAllChildMarkers().length, type, statuses);
        }

    }, [type]);

    const getStablePosition = useCallback((item) => {

        const raw = getPosition(item, type);
        if (!raw) return null;

        const lat = Number(raw[0]);
        const lon = Number(raw[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

        const id = item.hostid || item.complex || item.id || item.fid || item.name;
        if (!id) {
            // fallback: без id не сможем стабилизировать ссылку
            return [lat, lon];
        }

        const prev = positionCacheRef.current.get(id);
        if (prev && prev.lat === lat && prev.lon === lon) {
            return prev.pos;
        }

        const pos = [lat, lon];
        positionCacheRef.current.set(id, { lat, lon, pos });
        return pos;
    }, [type]);

    // Фильтруем по bounds как в LayerPillars (чтобы не держать лишние маркеры)
    const bounds = mapState.bounds;

    // Проверяем, что dataSource является массивом и bounds существует
    if (!Array.isArray(dataSource) || !bounds) {
        return null;
    }

    const nePixel = map.project(bounds.getNorthEast());
    const swPixel = map.project(bounds.getSouthWest());


    console.log(dataSource.filter(d => d.hostid === "10661"), type)
    let hosts = dataSource.map(item => {
        console.log(item, type)
        const position = getStablePosition(item);

        if (!position) {
            return null;
        }

        const markPixel = map.project(position)

        //Отбрасываем маркеры которые выходят за рамки видимой области
        if ((markPixel.x < swPixel.x) || (markPixel.x > nePixel.x) || (markPixel.y < nePixel.y) || (markPixel.y > swPixel.y)) return null

        if (!checkSeverityFilter(item, type, filter)) {
            return null;
        }

        return {
            ...item,
            position
        }
    }).filter(Boolean)


    const markers = hosts.map(item => {
        const triggerMaxPriority = type === 'vehicle' ? 0 : getTriggerMaxPriority(item);
        const pautinaMaxStatus = item.hasOwnProperty('pautina') ? getPautinaMaxStatus(item.pautina?.warnings) : null
        const zIndexOffset = getZIndexOffset(item, triggerMaxPriority, type);
        const icon = getMarkerIcon(item, triggerMaxPriority, type, pautinaMaxStatus, showSpeedLimit ? (parseInt(item.pautina?.data?.speed) || null) : null);

        return (
            <Marker
                position={item.position}
                icon={icon}
                key={item.hostid || item.complex}
                data={item.hostid || item.complex}
                trigger={triggerMaxPriority}
                zIndexOffset={zIndexOffset}
                pautinaStatus={pautinaMaxStatus}
                eventHandlers={{
                    click: handleMarkerClick(item)
                }}
            >
                {/* <SharedMarkerPopup 
                    complex={item} 
                    vehicle={type === 'vehicle' ? item : undefined}
                    notify={notify} 
                    type={type}
                /> */}


                {/* {shouldShowTooltip(item, type) && (
                    <MarkerTooltip 
                        complex={item} 
                        type={type}
                        key={`tooltip_${item.hostid}`}
                    />
                )} */}
                {showCameraDirection && item.pautina?.data?.azimut && mapState.zoom > 13 && (
                    <Polygon
                        positions={cameraTriangle(item.position, Number(item.pautina.data.azimut) - 180, 140 - (mapState.zoom * 2))}
                        pathOptions={{
                            color: 'black',
                            weight: 2,
                            fillColor: 'blue',
                            fillOpacity: 0.3,
                        }}
                    />
                )}
            </Marker>
        )
    })

    if (markers.length === 0) {
        return <></>;
    }

    if (!clustering) return markers

    return (
        <MarkerClusterGroup
            ref={clusterRef}
            maxClusterRadius={maxClusterRadius}
            iconCreateFunction={createClusterCustomIcon}
            removeOutsideVisibleBounds
            zoomToBoundsOnClick={true}
            spiderfyOnEveryZoom={false}
            showCoverageOnHover={false}
            animate={true}
            animateAddingMarkers={true}
            type={'cluster'}
            disableClusteringAtZoom={17}
            polygonOptions={polygonOptions}
            chunkedLoading={true}
            chunkInterval={200}
            chunkDelay={50}
        >
            {markers}
        </MarkerClusterGroup>
    );

};

export default Markers;

function cameraTriangle(position, azimut, size = 100) {
    console.log(position)
    const A = position;

    const B = destinationPoint(position[0], position[1], azimut - 15, size);
    const C = destinationPoint(position[0], position[1], azimut + 15, size);

    console.log([A, B, C])
    return [A, B, C];
}

function destinationPoint(lat, lng, bearing, distance) {
    const R = 6378137; // радиус Земли (м)
    const δ = distance / R; // угловое расстояние
    const θ = bearing * Math.PI / 180;

    const φ1 = lat * Math.PI / 180;
    const λ1 = lng * Math.PI / 180;

    const φ2 = Math.asin(
        Math.sin(φ1) * Math.cos(δ) +
        Math.cos(φ1) * Math.sin(δ) * Math.cos(θ)
    );

    const λ2 = λ1 + Math.atan2(
        Math.sin(θ) * Math.sin(δ) * Math.cos(φ1),
        Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2)
    );

    return [
        φ2 * 180 / Math.PI,
        λ2 * 180 / Math.PI
    ];
}
